/** @jsxImportSource @emotion/react */
import React, { useCallback } from 'react'
import {
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { status } from '@sportninja/common/constants/attendanceStatus'
import { MyPlayer } from '@sportninja/common/types/PlayerAttendance'
import { isRosterSpareConfirmed } from '@sportninja/common/features/game/attendance/utils/rosterSpareConfirm'
import {
  attendanceQuestionTextStyle,
  attendanceOptionsStyle,
  attendanceButtonStyle,
  iconStyle,
  spareNoticeStyle,
  spareNoticeIconStyle,
  spareNoticeTextStyle,
  attendancePlayerSeparatorStyle,
} from '../styles'

export interface AttendanceQuestionProps {
  playersAttendance: MyPlayer[]
  gameId: string
  onAttendanceSelect: (params: {
    playerId: string
    gameId: string
    rosterId: string
    status: number
  }) => void
}

const AttendanceQuestion: React.FC<AttendanceQuestionProps> = ({
  playersAttendance,
  gameId,
  onAttendanceSelect,
}) => {
  const handleAttendanceSelect = useCallback(
    (playerId: string, rosterId: string, selectedStatus: number) => {
      onAttendanceSelect({
        status: selectedStatus,
        playerId,
        rosterId,
        gameId,
      })
    },
    [onAttendanceSelect, gameId]
  )

  if (!playersAttendance || playersAttendance.length === 0) {
    return null
  }

  return (
    <>
      {playersAttendance.map((player, index) => {
        const isAttending = player?.attendance_status === status.ATTENDING
        const maybeAttending = player?.attendance_status === status.MAYBE
        const notAttending = player?.attendance_status === status.NOT_ATTENDING
        const isSpare = !!player?.is_spare
        // Once a team official confirms the spare, the "not confirmed yet"
        // notice no longer applies.
        const showSpareNotice = isSpare && !isRosterSpareConfirmed(player)

        return (
          <React.Fragment key={`${player.id}-${player.game_player_roster_id}`}>
            {index > 0 ? <div css={attendancePlayerSeparatorStyle} /> : null}
            {showSpareNotice ? (
              <div css={spareNoticeStyle}>
                <InfoCircleOutlined css={spareNoticeIconStyle} />
                <p css={spareNoticeTextStyle}>
                  Marking yourself available doesn&apos;t confirm you&apos;re
                  playing - a team official will confirm your spot.
                </p>
              </div>
            ) : null}
            <p css={attendanceQuestionTextStyle}>
              {isSpare
                ? `${player?.name_full}, are you available to play?`
                : `Will ${player?.name_full} attend the next game?`}
            </p>
            <div css={attendanceOptionsStyle}>
              <button
                css={attendanceButtonStyle(
                  isAttending ? colors.SUCCESS_200 : colors.SECONDARY_200,
                  isAttending ? colors.SUCCESS_200 : colors.NEUTRAL_100,
                  isAttending ? colors.WHITE : colors.NEUTRAL_50
                )}
                onClick={() =>
                  handleAttendanceSelect(
                    player?.id,
                    player?.game_player_roster_id,
                    status.ATTENDING
                  )
                }
              >
                <CheckCircleOutlined
                  css={iconStyle(
                    isAttending ? colors.WHITE : colors.NEUTRAL_50
                  )}
                />
                YES
              </button>
              {/* Spare players answer YES / NO only — hide MAYBE for them. */}
              {isSpare ? null : (
                <button
                  css={attendanceButtonStyle(
                    maybeAttending ? colors.WARNING_200 : colors.SECONDARY_200,
                    maybeAttending ? colors.WARNING_200 : colors.NEUTRAL_100,
                    maybeAttending ? colors.SECONDARY_300 : colors.NEUTRAL_50
                  )}
                  onClick={() =>
                    handleAttendanceSelect(
                      player?.id,
                      player?.game_player_roster_id,
                      status.MAYBE
                    )
                  }
                >
                  <QuestionCircleOutlined
                    css={iconStyle(
                      maybeAttending ? colors.SECONDARY_300 : colors.NEUTRAL_50
                    )}
                  />
                  MAYBE
                </button>
              )}
              <button
                css={attendanceButtonStyle(
                  notAttending ? colors.ERROR_200 : colors.SECONDARY_200,
                  notAttending ? colors.ERROR_200 : colors.NEUTRAL_100,
                  notAttending ? colors.WHITE : colors.NEUTRAL_50
                )}
                onClick={() =>
                  handleAttendanceSelect(
                    player?.id,
                    player?.game_player_roster_id,
                    status.NOT_ATTENDING
                  )
                }
              >
                <CloseCircleOutlined
                  css={iconStyle(
                    notAttending ? colors.WHITE : colors.NEUTRAL_50
                  )}
                />
                NO
              </button>
            </div>
          </React.Fragment>
        )
      })}
    </>
  )
}

export default React.memo(AttendanceQuestion)
